import { MdsIconKind } from "@/design-system/components/icon";
import { IItemPreviewState } from "@/design-system/components/item-list/rows/item-preview/types";
import { MdsItemDropdown } from "@/design-system/components/item-list/types";
import { ClassNamesArg } from "@/domains/emotion";
import { Uuid } from "@/domains/global/identifiers";
import { OnClick } from "@/domains/react/types";
import { ContactObservable } from "@/store/contacts/ContactObservable";
import { ReactNode } from "react";

export enum MdsItemKind {
  Collection = "COLLECTION",
  Note = "NOTE",
  Template = "TEMPLATE",
  Session = "SESSION",
  ActiveAudioCaptureNote = "ACTIVE_AUDIO_CAPTURE_NOTE",
  Search = "SEARCH",
  Message = "MESSAGE",
}

export enum MdsSharedByVariant {
  SharedBy = "SHARED_BY",
  AddedToCollectionBy = "ADDED_TO_COLLECTION_BY",
}

export type DynamicContent = () => ReactNode;

export interface ExtraRow {
  id: string;
  content: DynamicContent;
}

export interface TitleIcon {
  kind: MdsIconKind;
  tooltip?: string;
  innerStyles?: {
    Icon?: {
      className?: ClassNamesArg;
    };
  };
}

export interface ImageInfo {
  mimeType: string;
  url: string;
}

export interface SnippetChunk {
  text: string;
  isHighlight?: boolean;
  isTruncatedStart?: boolean;
  isTruncatedEnd?: boolean;
}

export interface TopicInfo {
  id: string;
  label: string;
}

export enum MdsCarouselItemVariant {
  Item = "ITEM",
  Collection = "COLLECTION",
  Other = "OTHER",
}

export interface MdsCarouselItem
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "onClick"> {
  id: string;
  variant: MdsCarouselItemVariant;
  primaryLabel: string;
  secondaryLabel?: string;
  icon: () => ReactNode;
  onClick: OnClick<{ itemId: string }>;
  isHidden?: boolean;
}

export interface IMdsListState {
  isHoverable: boolean;
  isSelectModeActive: boolean;
  canSelect: boolean;
  highlightedItemId?: string;
  highlightItem: (itemId: string) => void;
  isSelected: (itemId: string) => boolean;
  getNextItemId: (itemId: string) => string | undefined;
  selectItemsFromLastSelectionUntil: (itemId: string) => void;
  toggleItemSelection: (itemId: string) => void;
}

export type MdsItem<TKind extends MdsItemKind = MdsItemKind> = {
  kind: TKind;
  id: Uuid;
  label: string;
  createPreviewState?: () => IItemPreviewState;
  dateLabel?: string;
  onClick: OnClick<{ itemId: string; event?: React.MouseEvent | MouseEvent }>;
  description?: string;
  titleIcon?: TitleIcon;
  sharedBy?: ContactObservable;
  sharedByVariant?: MdsSharedByVariant;
  lastEditedDate?: string;
  listState?: IMdsListState;
  rightAlignedComponent?: (isHighlighted: boolean) => ReactNode;
  chips?: DynamicContent;
  extraRow?: ExtraRow;
  isSpecial?: boolean;
  dropdown?: MdsItemDropdown;
};

export type MdsCollectionItem = MdsItem<MdsItemKind.Collection>;
export type MdsNoteItem = MdsItem<MdsItemKind.Note>;
